/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { Button, Center, Group, Stack, Text, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { ROUTES } from './router';

function getRouteErrorMessage(error: unknown): string | undefined {
  if (isRouteErrorResponse(error)) {
    return error.statusText ?? error.status?.toString();
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const value = (error as { message?: unknown }).message;
    return typeof value === 'string' ? value : undefined;
  }
  return undefined;
}

export function RouteErrorBoundary() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const error = useRouteError();
  const message = getRouteErrorMessage(error);

  return (
    <Center h="50vh" p="xl">
      <Stack align="center" maw={580} w="100%">
        <Title order={2}>{t('error_title')}</Title>
        <Text c="dimmed">{t('error_description')}</Text>
        {message ? (
          <Text size="sm" c="dimmed" ta="center" style={{ maxWidth: 520 }}>
            {message}
          </Text>
        ) : null}
        <Group>
          <Button component={Link} to={ROUTES.dashboard} size="md">
            {t('error_goHome')}
          </Button>
          <Button variant="outline" size="md" onClick={() => navigate(0)}>
            {t('error_reload')}
          </Button>
        </Group>
      </Stack>
    </Center>
  );
}
